<template>
    <main id="login" class="w-100 h-100">
        <div v-if="passwordlessLoginToken && !passwordlessLoginUnsuccessful" class="loading-spinner"></div>
        <div v-else class="d-flex justify-center justify-content-center">
            <div class="content d-flex flex-column justify-content-center align-items-center content-wrapper">
                <div class="content-form">
                    <div class="header-container">
                        <img src="https://static.supsis.live/common/supsisAiLogo.png" style="width: 150px" />
                        <language-switch id="selectLang" />
                    </div>
                    <div class="login-title">
                        <div class="title-text">
                            {{ $t("login.loginTitle1") }}
                            <div>{{ $t("login.loginTitle2") }}</div>
                        </div>
                    </div>
                    <div style="margin-top: 40px">
                        <div>
                            <span class="font-weight-bold email" style="color: #183b56">{{ $t("login.email") }}</span>
                            <div style="margin-top: 3px">
                                <a-form-item
                                    has-feedback
                                    :validate-status="
                                        loginForm.email ? (!!errors.first('email') ? 'error' : 'success') : undefined
                                    "
                                    :help="
                                        loginForm.email && errors.first('email') ? $t('login.emailError') : undefined
                                    "
                                >
                                    <a-input
                                        ref="userNameInput"
                                        v-model="loginForm.email"
                                        v-validate="'required|email'"
                                        @keydown.enter="doLogin()"
                                        name="email"
                                        :class="{ error: !!errors.first('email') }"
                                        :placeholder="$t('login.emailPlaceholder')"
                                    >
                                        <a-icon slot="prefix" type="mail" id="mailIcon" />
                                    </a-input>
                                </a-form-item>
                            </div>
                        </div>
                        <div style="margin-top: 15px">
                            <span class="font-weight-bold" style="color: #183b56">{{ $t("login.password") }}</span>
                            <div style="margin-top: 3px">
                                <a-input
                                    ref="userNameInput"
                                    v-model="loginForm.password"
                                    @keydown.enter="doLogin()"
                                    :type="loginForm.hidePassword ? 'text' : 'password'"
                                    :placeholder="$t('login.passwordPlaceholder')"
                                >
                                    <a-icon slot="prefix" type="lock" />
                                    <a-icon
                                        slot="suffix"
                                        :type="loginForm.hidePassword ? 'eye' : 'eye-invisible'"
                                        style="color: rgba(0, 0, 0, 0.45)"
                                        @click="loginForm.hidePassword = !loginForm.hidePassword"
                                    />
                                </a-input>
                            </div>
                        </div>
                        <div class="user-hash d-flex justify-content-between flex-wrap" style="margin-top: 15px">
                            <div>
                                <a-checkbox v-model="loginForm.rememberme" @change="$_.noop">
                                    {{ $t("login.loginMemory") }}
                                </a-checkbox>
                            </div>
                            <router-link to="/forgot-password">
                                <div style="color: #ec4b1d">{{ $t("login.passwordForget") }}</div>
                            </router-link>
                        </div>
                        <div class="d-flex justify-content-between" style="margin-top: 20px">
                            <div
                                :style="{
                                    background: !errors.first('email') && loginForm.password ? '#ec4b1d' : '#C3CAD9',
                                    cursor: !errors.first('email') && loginForm.password ? 'pointer' : null,
                                }"
                                class="w-100 text-center text-white font-weight-bold user-select-none"
                                style="border-radius: 8px; padding: 13px 0; font-size: 15px"
                                @click="!errors.first('email') && loginForm.password ? doLogin() : null"
                            >
                                {{ $t("login.login") }}
                            </div>
                        </div>
                        <div class="d-flex justify-content-between align-items-center" style="margin-top: 20px">
                            <hr color="#C3CAD9" width="25%" />
                            <div style="margin: 0px 5px; color: #183b56" width="100%">
                                {{ $t("login.loginFooter") }}
                            </div>
                            <hr color="#C3CAD9" width="25%" />
                        </div>
                        <div style="margin-top: 20px; display: flex; justify-content: space-between; width: 100%">
                            <router-link
                                class="text-center text-white w-100 font-weight-bold"
                                style="background: #183b56; border-radius: 8px; padding: 13px 0; font-size: 15px"
                                to="/passwordless-login"
                            >
                                {{ $t("login.emailLogin") }}
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import { mapActions, mapState } from "vuex";
import SupsisLogo from "../../components/svg/SupsisLogo.vue";
import PasswordForgot from "../../components/svg/PasswordForgot";
import log2 from "../../utils/log2";
import LanguageSwitch from "../../components/LanguageSwitch";

const log = log2("Login");
export default {
    name: "Login",
    components: { SupsisLogo, LanguageSwitch, PasswordForgot },

    data() {
        return {
            passwordlessLoginToken: this.$route.query.passwordlessLoginToken,
            forceResetPassword: this.$route.query.forceResetPassword === "true",
            emailFromQuery: decodeURIComponent(this.$route.query.email),
            retryCount: 0,
            maxRetries: 3,
            retryDelay: 1000,
            loading: false,
            isSubmit: false,
            passwordlessLoginUnsuccessful: false,
            loginForm: {
                hidePassword: false,
                rememberme: true,
                email: "",
                password: "",
            },
            username: null,
        };
    },
    methods: {
        ...mapActions({
            login: "auth/login",
            passwordlessLogin: "auth/passwordlessLogin",
        }),
        goBackToLogin() {
            this.passwordlessLoginToken = null;
            this.emailFromQuery = null;
            this.retryCount = 0;
            this.passwordlessLoginUnsuccessful = false;
            this.$router.push("login");
        },
        doLogin() {
            this.isSubmit = true;
            this.$validator.validateAll().then((response) => {
                if (response) {
                    this.loading = true;
                    this.login(this.loginForm)
                        .then((loggedUser) => {
                            log("[Router] user logged in forwarding to /");
                            const redirectUrl = this.$route.query.redirect;
                            if (redirectUrl) {
                                this.$router.push(redirectUrl);
                            } else {
                                this.$router.push("/");
                            }
                            if (this.loginForm.rememberme) {
                            } else {
                            }
                        })
                        .catch((err) => {
                            const errMsg =
                                err.response && err.response.data && err.response.data.localizedMessages
                                    ? err.response.data.localizedMessages[this.$i18n.locale]
                                    : err.message;
                            const trTimeout = "İstek zaman aşımına uğradı";
                            const enTimeout = "The request timed out";
                            const langTimeout = this.language.code === "tr" ? trTimeout : enTimeout;
                            if (errMsg === "timeout of 20000ms exceeded" ? langTimeout : errMsg) {
                                this.$bvToast.toast(errMsg, {
                                    title: this.$t("message.error"),
                                    variant: "danger",
                                    solid: true,
                                });
                            }
                            this.passwordlessLoginToken = null;
                        })
                        .finally(() => (this.loading = false));
                }
            });
        },
        doPasswordlessLogin(isRetry = false) {
            if (this.passwordlessLoginToken && this.emailFromQuery) {
                this.loading = true;
                this.passwordlessLogin({
                    email: this.emailFromQuery,
                    passwordlessLoginToken: this.passwordlessLoginToken,
                })
                    .then((loggedUser) => {
                        this.retryCount = 0;
                        const redirectUrl = this.$route.query.redirect;


                        let url;
                        if (this.forceResetPassword) {
                            url = `/?ptoken=${this.passwordlessLoginToken}&forceResetPassword=${this.forceResetPassword}`;
                        } else if (redirectUrl) {
                            url = redirectUrl;
                        } else {
                            url = "/";
                        }

                        log("[Router] user logged via passwordless in forwarding to url", url);
                        this.$router.push(url);
                    })
                    .catch((err) => {
                        console.error('Passwordless login attempt failed:', err);
                        
                        if (this.retryCount < this.maxRetries) {
                            this.retryCount++;
                            console.log(`Retrying passwordless login (${this.retryCount}/${this.maxRetries})...`);
                            
                            setTimeout(() => {
                                this.doPasswordlessLogin(true);
                            }, this.retryDelay);
                        } else {
                            this.passwordlessLoginUnsuccessful = true;
                            this.loading = false;
                        }
                    })
                    .finally(() => {
                        if (this.retryCount >= this.maxRetries || this.retryCount === 0) {
                            this.loading = false;
                        }
                    });
            }
        },
    },
    computed: {
        location() {
            return document.location.hostname;
        },
        ...mapState({
            site: (state) => state.site.site,
            language: (state) => state.language.language,
            languages: (state) => state.language.languages,
        }),
    },
    watch: {
        site(newValue) {
            if (newValue && newValue._id) {
                this.doPasswordlessLogin();
            }
        },
    },
    mounted() {
        if (this.site) {
            this.doPasswordlessLogin();
        }
    },
};
</script>

<style lang="scss" scoped>
.loading-spinner {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 50px;
    height: 50px;
    border: 5px solid rgba(0, 0, 0, 0.1);
    border-top: 5px solid #ec4b1d;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: translate(-50%, -50%) rotate(0deg);
    }
    100% {
        transform: translate(-50%, -50%) rotate(360deg);
    }
}

::-moz-selection {
    /* Code for Firefox */
    color: white !important;
    background: #ec4b1d !important;
}

::selection {
    color: white !important;
    background: #ec4b1d !important;
}

@media (max-width: 724px) {
    .content-form {
        width: 80% !important;
    }
}

@media (max-width: 950px) {
    .user-hash {
        flex-direction: row;
    }
    .content {
        flex-direction: row;
        width: 100% !important;
    }

    .right-side {
        display: none !important;
    }
}

.passwordless-login {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    margin: 5px;
}

.login-error {
    margin: 2em;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

h3 {
    margin: 2em;
    color: #ffffff;
}

.login-btn-rr {
    border-radius: 30.5px;
    background-color: #48b6e7;
    margin: 2em;
    border: none !important;
}

.content-wrapper {
    width: 45%;
    margin-top: 50px;
    transition: 1s all;
}

.content-form {
    width: 60%;
}

.header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.login-title {
    margin-top: 50px;
    color: #183b56;
    font-weight: 700;
    font-size: 35px;
}

.title-text {
    line-height: 1;
}
</style>
